import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Loader2, Trophy, Gift, Clock, Check, Ban, ScanLine } from "lucide-react";
import { toast } from "sonner";

function RewardImage({ rewardId, className = "" }) {
  const [url, setUrl] = useState("");
  useEffect(() => {
    let revoke = null;
    (async () => {
      try {
        const resp = await api.get(`/rewards/${rewardId}/image`, { responseType: "blob" });
        const u = URL.createObjectURL(resp.data);
        revoke = u;
        setUrl(u);
      } catch {}
    })();
    return () => { if (revoke) URL.revokeObjectURL(revoke); };
  }, [rewardId]);
  if (!url) {
    return (
      <div className={`flex items-center justify-center bg-zinc-900 border border-zinc-800 ${className}`}>
        <Loader2 className="h-5 w-5 text-zinc-600 animate-spin" />
      </div>
    );
  }
  return <img src={url} alt="Recompensa" className={`object-cover ${className}`} />;
}

export default function CustomerLoyalty() {
  const [balance, setBalance] = useState({ balance: 0, pending_redeemed: 0, available: 0, history: [] });
  const [rewards, setRewards] = useState([]);
  const [redemptions, setRedemptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cfg, setCfg] = useState({ points_per_amount: 1, amount_unit: 10 });
  const [confirmReward, setConfirmReward] = useState(null);
  const [redeeming, setRedeeming] = useState(false);
  const [codeDialog, setCodeDialog] = useState(null);

  const load = async () => {
    try {
      setLoading(true);
      const [b, rw, rd, c] = await Promise.all([
        api.get("/loyalty/balance"),
        api.get("/rewards"),
        api.get("/redemptions"),
        api.get("/loyalty/config"),
      ]);
      setBalance(b.data);
      setRewards(rw.data);
      setRedemptions(rd.data);
      setCfg(c.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const openConfirm = (r) => setConfirmReward(r);

  const redeem = async () => {
    if (!confirmReward) return;
    setRedeeming(true);
    try {
      const { data } = await api.post("/redemptions", { reward_id: confirmReward.id });
      setRedemptions((p) => [data, ...p]);
      setBalance((b) => ({ ...b, pending_redeemed: b.pending_redeemed + data.points_cost, available: Math.max(0, b.available - data.points_cost) }));
      setConfirmReward(null);
      setCodeDialog(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setRedeeming(false);
    }
  };

  const pendingList = redemptions.filter((r) => r.status === "pending");
  const historyList = redemptions.filter((r) => r.status !== "pending");

  if (loading) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-amber-brand" /></div>;
  }

  return (
    <div className="space-y-6 animate-fade-up">
      <div>
        <div className="text-[11px] font-mono-caps text-zinc-500">Clube de pontos</div>
        <h1 className="font-display text-3xl sm:text-4xl font-black uppercase text-zinc-100 flex items-center gap-2">
          <Trophy className="h-7 w-7 text-amber-brand" /> Meus pontos
        </h1>
      </div>

      <div
        data-testid="loyalty-balance-card"
        className="rounded-xl border border-amber-brand/40 bg-gradient-to-br from-amber-brand/10 via-zinc-950 to-zinc-950 p-5 md:p-7"
      >
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
          <div>
            <div className="text-[11px] font-mono-caps text-amber-200">Saldo disponível</div>
            <div className="font-display text-5xl sm:text-6xl font-black text-amber-brand mt-1" data-testid="loyalty-available">
              {balance.available}
              <span className="text-xl text-amber-200/70 ml-2">pts</span>
            </div>
            <div className="text-xs text-zinc-400 mt-2">
              Saldo total: <span className="text-zinc-200 font-bold" data-testid="loyalty-balance-total">{balance.balance}</span>
              {balance.pending_redeemed > 0 && (
                <> · Reservado em resgates: <span className="text-amber-300 font-bold" data-testid="loyalty-pending-amount">{balance.pending_redeemed}</span></>
              )}
            </div>
          </div>
          <div className="text-right">
            <div className="text-[11px] font-mono-caps text-zinc-500">Régua da oficina</div>
            <div className="text-sm text-zinc-300 font-bold" data-testid="loyalty-rule">
              {cfg.points_per_amount} {cfg.points_per_amount === 1 ? "ponto" : "pontos"} a cada R$ {Number(cfg.amount_unit).toFixed(2).replace(".", ",")}
            </div>
            <div className="text-[11px] text-zinc-500 mt-0.5">creditado após o serviço concluído</div>
          </div>
        </div>
      </div>

      {pendingList.length > 0 && (
        <section data-testid="pending-redemptions-section" className="space-y-3">
          <div className="flex items-center gap-2">
            <Clock className="h-4 w-4 text-amber-brand" />
            <h2 className="font-display text-sm font-bold uppercase text-amber-brand tracking-wider">Resgates pendentes</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {pendingList.map((r) => (
              <div key={r.id} data-testid={`pending-redemption-${r.id}`} className="rounded-xl border border-amber-brand/40 bg-amber-brand/5 p-4">
                <div className="flex items-center justify-between gap-2">
                  <code
                    data-testid={`pending-code-${r.id}`}
                    className="text-base font-mono-caps font-black bg-zinc-950 text-amber-brand border border-amber-brand/60 rounded px-3 py-1 tracking-widest"
                  >{r.code}</code>
                  <span className="text-xs font-bold text-amber-brand">{r.points_cost} pts</span>
                </div>
                <div className="mt-2 text-zinc-100 font-semibold">{r.reward_name}</div>
                <div className="text-xs text-zinc-500 mt-1">
                  Apresente este código na oficina para validar o resgate.
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="space-y-3">
        <div className="flex items-center gap-2">
          <Gift className="h-4 w-4 text-amber-brand" />
          <h2 className="font-display text-sm font-bold uppercase text-amber-brand tracking-wider">Recompensas disponíveis</h2>
        </div>
        {rewards.length === 0 ? (
          <div data-testid="customer-rewards-empty" className="rounded-xl border border-dashed border-zinc-800 bg-zinc-950/40 p-10 text-center">
            <Gift className="h-10 w-10 mx-auto text-zinc-700" />
            <p className="mt-3 text-zinc-400 font-medium">A oficina ainda não cadastrou recompensas.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {rewards.map((r) => {
              const canAfford = balance.available >= r.points_cost;
              return (
                <article
                  key={r.id}
                  data-testid={`customer-reward-${r.id}`}
                  className={`rounded-xl border overflow-hidden flex flex-col ${canAfford ? "border-amber-brand/40 bg-amber-brand/5 hover:border-amber-brand transition-colors" : "border-zinc-800 bg-zinc-950/70 opacity-80"}`}
                >
                  {r.has_image ? (
                    <RewardImage rewardId={r.id} className="h-36 w-full" />
                  ) : (
                    <div className="h-36 w-full flex items-center justify-center bg-gradient-to-br from-amber-900/40 to-zinc-900">
                      <Gift className="h-10 w-10 text-amber-brand/70" />
                    </div>
                  )}
                  <div className="p-4 flex-1 flex flex-col">
                    <h3 className="font-display text-lg font-bold text-zinc-100 line-clamp-2">{r.name}</h3>
                    {r.description && <p className="text-sm text-zinc-400 mt-1 line-clamp-3">{r.description}</p>}
                    <div className="mt-auto pt-3 flex items-center justify-between gap-2">
                      <span className="inline-flex items-center gap-1 text-amber-brand font-bold">
                        <Trophy className="h-4 w-4" /> {r.points_cost} pts
                      </span>
                      <Button
                        data-testid={`redeem-btn-${r.id}`}
                        onClick={() => openConfirm(r)}
                        disabled={!canAfford}
                        className="h-9 bg-amber-brand hover:bg-amber-brandDark text-black font-bold uppercase disabled:bg-zinc-800 disabled:text-zinc-500"
                      >
                        {canAfford ? "Resgatar" : "Faltam pontos"}
                      </Button>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </section>

      {historyList.length > 0 && (
        <section data-testid="redemption-history-section" className="space-y-3">
          <div className="flex items-center gap-2">
            <ScanLine className="h-4 w-4 text-zinc-400" />
            <h2 className="font-display text-sm font-bold uppercase text-zinc-400 tracking-wider">Histórico de resgates</h2>
          </div>
          <ul className="rounded-xl border border-zinc-800 bg-zinc-950/70 divide-y divide-zinc-800">
            {historyList.map((r) => (
              <li key={r.id} data-testid={`history-redemption-${r.id}`} className="p-3 flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-sm text-zinc-100 font-semibold truncate">{r.reward_name}</div>
                  <div className="text-[11px] text-zinc-500" translate="no">
                    <code className="font-mono-caps">{r.code}</code>
                    <span className="mx-1">·</span>
                    <span>{r.points_cost} pts</span>
                    {r.created_at && (<><span className="mx-1">·</span><span>{new Date(r.created_at).toLocaleDateString("pt-BR")}</span></>)}
                  </div>
                </div>
                {r.status === "confirmed" ? (
                  <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-green-300 bg-green-brand/15 border border-green-brand/40 rounded-full px-2 py-0.5">
                    <Check className="h-3 w-3" /> Confirmado
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-red-300 bg-red-950/40 border border-red-900 rounded-full px-2 py-0.5">
                    <Ban className="h-3 w-3" /> Cancelado
                  </span>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      <Dialog open={!!confirmReward} onOpenChange={(o) => !o && setConfirmReward(null)}>
        <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display uppercase flex items-center gap-2">
              <Gift className="h-5 w-5 text-amber-brand" /> Confirmar resgate
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              Vamos gerar um código para você apresentar na oficina. Os pontos só são debitados após o dono confirmar.
            </DialogDescription>
          </DialogHeader>
          {confirmReward && (
            <div className="rounded-xl border border-zinc-800 bg-zinc-900/60 p-4">
              <div className="font-display text-lg font-bold text-zinc-100">{confirmReward.name}</div>
              {confirmReward.description && <p className="text-sm text-zinc-400 mt-1">{confirmReward.description}</p>}
              <div className="mt-2 text-amber-brand font-bold flex items-center gap-1">
                <Trophy className="h-4 w-4" /> {confirmReward.points_cost} pts
              </div>
            </div>
          )}
          <DialogFooter>
            <Button type="button" variant="ghost" disabled={redeeming} onClick={() => setConfirmReward(null)} className="text-zinc-300 hover:bg-zinc-900">
              Cancelar
            </Button>
            <Button
              data-testid="confirm-redeem-btn"
              onClick={redeem}
              disabled={redeeming}
              className="bg-amber-brand hover:bg-amber-brandDark text-black font-bold uppercase"
            >
              {redeeming ? <Loader2 className="h-4 w-4 animate-spin" /> : "Pedir resgate"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!codeDialog} onOpenChange={(o) => !o && setCodeDialog(null)}>
        <DialogContent className="bg-zinc-950 border-amber-brand/40 text-zinc-100 max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display uppercase flex items-center gap-2">
              <Trophy className="h-5 w-5 text-amber-brand" /> Resgate pedido!
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              Apresente este código na oficina. Os pontos ficam reservados até a confirmação do dono.
            </DialogDescription>
          </DialogHeader>
          {codeDialog && (
            <div className="text-center py-6">
              <div className="text-[11px] font-mono-caps text-amber-200 mb-2">Seu código</div>
              <code
                data-testid="new-redemption-code"
                className="inline-block text-3xl font-mono-caps font-black bg-zinc-950 text-amber-brand border border-amber-brand/60 rounded-xl px-6 py-3 tracking-widest"
              >
                {codeDialog.code}
              </code>
              <div className="mt-4 text-sm text-zinc-300">{codeDialog.reward_name}</div>
              <div className="text-xs text-amber-200/80 mt-1">{codeDialog.points_cost} pts (reservados)</div>
            </div>
          )}
          <DialogFooter>
            <Button
              data-testid="code-dialog-close"
              onClick={() => setCodeDialog(null)}
              className="w-full bg-green-brand hover:bg-green-brandDark text-white font-bold uppercase"
            >
              Entendi
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
